class DealBundle extends HTMLElement {
  constructor() {
    super();
    this.bars = this.querySelectorAll(".deal-bundle__bar");
    this.productHandle = this.dataset.productHandle;
    this.productData = null; // Store product data for reuse
    this.productDataCache = {}; // Cache for different products
    this.init();
  }

  async init() {
    this.addBarListeners();

    // Load product data for all bars
    await this.loadAllProductData();
    this.initializeBars();
    this.addCartButtonListener();

    // Listen for the color:switch event
    document.addEventListener("color:switch", (e) => this.handleColorSwitch(e));
  }

  // Handle color:switch event
  handleColorSwitch(event) {
    console.log("color:switch event triggered", event.detail);

    const { name: colorName, id: variantId } = event.detail;

    // Get the selected bar
    const selectedBar = this.getSelectedBar();
    if (!selectedBar) return;

    // Find the first color option in the selected bar
    const colorOption = selectedBar.querySelector(
      '.deal-bundle-option[data-name="Color"]'
    );
    if (colorOption) {
      // Update the display with the new color
      const selectedDisplay = colorOption.querySelector(
        ".deal-bundle-selected-display"
      );

      // Optionally, fire a click on the color option to update the selection (if you prefer)
      const colorOptionValue = colorOption.querySelector(
        `[data-value="${colorName}"]`
      );
      if (colorOptionValue) {
        colorOptionValue.click();
      }
    }
  }

  getSelectedBar() {
    return Array.from(this.bars).find((bar) =>
      bar.classList.contains("deal-bundle__bar--selected")
    );
  }

  // Utility: Add click listeners to bars
  addBarListeners() {
    this.bars.forEach((bar) => {
      bar.addEventListener("click", () => this.selectBar(bar));
    });
  }

  // Load all product data for all bars
  async loadAllProductData() {
    const uniqueHandles = new Set();

    // Collect all unique product handles from bars
    this.bars.forEach((bar) => {
      const handle = bar.dataset.productHandle;
      if (handle) {
        uniqueHandles.add(handle);
      }
    });

    // Add the main product handle as fallback
    if (this.productHandle) {
      uniqueHandles.add(this.productHandle);
    }

    // Fetch all unique products
    try {
      await Promise.all(
        Array.from(uniqueHandles).map(async (handle) => {
          if (!this.productDataCache[handle]) {
            this.productDataCache[handle] = await this.fetchProductData(handle);
          }
        })
      );

      // Set the main product data for backwards compatibility
      this.productData = this.productDataCache[this.productHandle];
    } catch (error) {
      console.error("Error loading product data:", error);
    }
  }

  // Utility: Fetch product data
  async fetchProductData(handle) {
    const response = await fetch(`/products/${handle}.json`);
    if (!response.ok) {
      throw new Error(`Error loading product with handle: ${handle}`);
    }
    const { product } = await response.json();
    return product;
  }

  // Initialize bars with product data
  initializeBars() {
    this.bars.forEach((bar) => {
      this.updatePrice(bar);
      this.initDropdown(bar);

      // Check if this bar is initially selected
      if (bar.classList.contains("deal-bundle__bar--selected")) {
        this.updateGalleryImage(bar);
      }
    });
  }

  // Handle bar selection
  selectBar(selectedBar) {
    let hasSelection = false;

    this.bars.forEach((bar) => {
      const isSelected = bar === selectedBar;
      bar.classList.toggle("deal-bundle__bar--selected", isSelected);

      if (isSelected) {
        this.updatePrice(bar);
        this.updateGalleryImage(bar);
        this.updateTitle(bar);
        hasSelection = true;
      }
    });

    // If no bar is selected, restore original gallery image
    if (!hasSelection) {
      this.restoreOriginalGalleryImage();
    }
  }

  updateTitle(bar) {
    const title = document.querySelector(".product-info__title");
    const newTitle = bar.dataset.title;
    if (!title) return;
    title.textContent = newTitle;
  }

  // Update the gallery with the bar's image
  updateGalleryImage(bar) {
    const variantsGrid = bar.querySelector(".deal-bundle__bar-variants-grid");
    if (!variantsGrid) return;

    const imageUrl = variantsGrid.dataset.image;
    if (!imageUrl || imageUrl === "") return;

    // Find the product gallery
    const productGallery = document.querySelector("product-gallery");
    if (!productGallery) return;

    const mediaCarousel = productGallery.querySelector("media-carousel");
    if (!mediaCarousel) return;

    // Find the first media element in the carousel
    const firstMedia = mediaCarousel.querySelector(".product-gallery__media");
    if (!firstMedia) return;

    // Find the img element within the first media
    const img = firstMedia.querySelector("img");
    if (!img) return;

    // Store original src if not already stored
    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.src;
      img.dataset.originalSrcset = img.srcset || "";
      img.dataset.originalAlt = img.alt || "";
    }

    // Update the image with deal-bundle image
    img.src = imageUrl;
    img.srcset = `${imageUrl} 1x`;
    img.alt = "Deal Bundle Image";

    // Mark the first media as deal-bundle controlled
    firstMedia.setAttribute("data-deal-bundle-controlled", "true");

    // Update the first thumbnail as well
    this.updateFirstThumbnail(productGallery, imageUrl);

    // Make sure the first image is selected
    if (mediaCarousel.select) {
      mediaCarousel.select(0, { animate: false });
    }
  }

  // Update the first thumbnail with deal-bundle image
  updateFirstThumbnail(productGallery, imageUrl) {
    const thumbnailContainer = productGallery.querySelector(
      "page-dots[aria-controls]"
    );
    if (!thumbnailContainer) return;

    // Find the first thumbnail
    const firstThumbnail = thumbnailContainer.querySelector(
      ".product-gallery__thumbnail"
    );
    if (!firstThumbnail) return;

    // Find the img element within the first thumbnail
    const img = firstThumbnail.querySelector("img");
    if (!img) return;

    // Store original src if not already stored
    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.src;
      img.dataset.originalSrcset = img.srcset || "";
      img.dataset.originalAlt = img.alt || "";
    }

    // Update the thumbnail image
    img.src = imageUrl;
    img.srcset = `${imageUrl} 1x`;
    img.alt = "Deal Bundle Thumbnail";

    // Mark the first thumbnail as deal-bundle controlled
    firstThumbnail.setAttribute("data-deal-bundle-controlled", "true");
  }

  // Restore original gallery image
  restoreOriginalGalleryImage() {
    const productGallery = document.querySelector("product-gallery");
    if (!productGallery) return;

    const mediaCarousel = productGallery.querySelector("media-carousel");
    if (!mediaCarousel) return;

    // Find the first media element
    const firstMedia = mediaCarousel.querySelector(".product-gallery__media");
    if (!firstMedia || !firstMedia.hasAttribute("data-deal-bundle-controlled"))
      return;

    const img = firstMedia.querySelector("img");
    if (!img || !img.dataset.originalSrc) return;

    // Restore original image
    img.src = img.dataset.originalSrc;
    img.srcset = img.dataset.originalSrcset;
    img.alt = img.dataset.originalAlt;

    // Remove deal-bundle control marker
    firstMedia.removeAttribute("data-deal-bundle-controlled");

    // Restore original thumbnail
    this.restoreOriginalThumbnail(productGallery);
  }

  // Restore original thumbnail image
  restoreOriginalThumbnail(productGallery) {
    const thumbnailContainer = productGallery.querySelector(
      "page-dots[aria-controls]"
    );
    if (!thumbnailContainer) return;

    const firstThumbnail = thumbnailContainer.querySelector(
      ".product-gallery__thumbnail"
    );
    if (
      !firstThumbnail ||
      !firstThumbnail.hasAttribute("data-deal-bundle-controlled")
    )
      return;

    const img = firstThumbnail.querySelector("img");
    if (!img || !img.dataset.originalSrc) return;

    // Restore original thumbnail
    img.src = img.dataset.originalSrc;
    img.srcset = img.dataset.originalSrcset;
    img.alt = img.dataset.originalAlt;

    // Remove deal-bundle control marker
    firstThumbnail.removeAttribute("data-deal-bundle-controlled");
  }

  initDropdown(bar) {
    const dropdowns = bar.querySelectorAll(".deal-bundle-option");

    // Handle clicks on dropdowns
    dropdowns.forEach((dropdown) => {
      const selectedDisplay = dropdown.querySelector(
        ".deal-bundle-selected-display"
      );
      const dropdownList = dropdown.querySelector(".deal-bundle-dropdown");
      const options = dropdownList.querySelectorAll(
        ".deal-bundle-option-value"
      );

      // Toggle active class on dropdown click
      dropdown.addEventListener("click", (e) => {
        e.stopPropagation(); // Prevent event from bubbling up to document
        dropdown.classList.toggle("active"); // Toggle current dropdown
      });

      // Handle click on list items
      options.forEach((option) => {
        option.addEventListener("click", (e) => {
          e.stopPropagation(); // Prevent bubbling to document
          if (!option.hasAttribute("disabled")) {
            // Update the selected display with the chosen value
            selectedDisplay.textContent = option.getAttribute("data-value");
            selectedDisplay.setAttribute(
              "data-value",
              option.getAttribute("data-value")
            );
            dropdown.classList.remove("active");

            // Track the last selected option

            // If the option is for color, move the color swatch span into the selected field
            const colorSpan = option.querySelector(".deal-bundle_option-color");
            if (colorSpan) {
              // Clone the color swatch and move it to the selected display
              const clonedSwatch = colorSpan.cloneNode(true);
              const existingSwatch = selectedDisplay.querySelector(
                ".deal-bundle_option-color"
              );
              if (existingSwatch) {
                existingSwatch.replaceWith(clonedSwatch); // Replace if exists
              } else {
                selectedDisplay.prepend(clonedSwatch); // Append if not exists
              }

              // Dispatch the color switch event for the selected color
              const color = option.getAttribute("data-value");
              const variantId = this.findMatchingVariant(bar).id;
              this.dispatchColorSelectedEvent(color, variantId);
            }
          }
        });
      });
    });
  }

  // Update price based on selected variants
  updatePrice(bar) {
    const productHandle = bar.dataset.productHandle;
    const productData = this.productDataCache[productHandle];
    if (!productData) return;

    const discount = bar.dataset.discount;
    const variantSelects = bar.querySelectorAll(
      ".deal-bundle__bar-variant-selects"
    );
    const totalPrice = Array.from(variantSelects).reduce((sum, select) => {
      const matchingVariant = this.findMatchingVariant(select, productData);
      return sum + (matchingVariant ? parseFloat(matchingVariant.price) : 0);
    }, 0);
    const totalComparePrice = Array.from(variantSelects).reduce(
      (sum, select) => {
        const matchingVariant = this.findMatchingVariant(select, productData);
        return (
          sum +
          (matchingVariant ? parseFloat(matchingVariant.compare_at_price) : 0)
        );
      },
      0
    );

    const discountPrice = totalPrice - Number(discount);
    const priceElement = bar.querySelector(".deal-bundle__bar-price");
    if (priceElement) {
      priceElement.textContent = `${discountPrice.toFixed(2)}€`;
    }
    //const priceCompareElement = bar.querySelector('.deal-bundle__bar-full-price');
    //if (priceCompareElement) {
    //priceCompareElement.textContent = `${totalComparePrice.toFixed(2)}€`;
    //}
  }

  findMatchingVariant(selectGroup, productData = null) {
    const selectedOptions = {};

    // Use provided productData or find it from the bar
    let currentProductData = productData;
    if (!currentProductData) {
      const bar = selectGroup.closest(".deal-bundle__bar");
      const productHandle = bar
        ? bar.dataset.productHandle
        : this.productHandle;
      currentProductData =
        this.productDataCache[productHandle] || this.productData;
    }

    if (!currentProductData) return null;

    // Iterate through all selects within the group
    selectGroup
      .querySelectorAll(".deal-bundle__bar-variant-select")
      .forEach((select) => {
        const position = select.dataset.position
          ? parseInt(select.dataset.position, 10)
          : null; // Get position safely
        const selectedValue = select.querySelector(
          ".deal-bundle-selected-display"
        ).dataset.value; // Get selected value

        // Ensure position is valid before adding to selected options
        if (!isNaN(position) && position >= 1 && position <= 3) {
          selectedOptions[`option${position}`] = selectedValue;
        } else {
          console.warn(
            `Invalid data-position "${position}" on select element`,
            select
          );
        }
      });

    const variant = currentProductData.variants.find((variant) =>
      Object.entries(selectedOptions).every(
        ([key, value]) => variant[key] === value
      )
    );

    return variant;
  }

  dispatchColorSelectedEvent(colorName, variantId) {
    const colorSwitchEvent = new CustomEvent("color:switch", {
      detail: {
        id: variantId.toString(),
        name: colorName,
      },
    });
    document.dispatchEvent(colorSwitchEvent);
  }

  addCartButtonListener() {
    const addToCartButton = document.querySelector(".deal-bundle__atc");
    if (!addToCartButton) return;

    addToCartButton.addEventListener("click", async () => {
      const selectedBar = this.getSelectedBar();
      if (!selectedBar) {
        alert("Please select a deal to add to the cart.");
        return;
      }

      const variantsToAdd = this.getSelectedVariants(selectedBar);

      // Show loader on button
      addToCartButton.classList.add("loading");

      if (variantsToAdd.length > 0) {
        try {
          await this.addToCart(variantsToAdd);
          this.themeCart();
        } catch (error) {
          console.error("Error adding to cart:", error);
        } finally {
          // Hide loader after the action is complete
          addToCartButton.classList.remove("loading");
        }
      } else {
        alert("Please select all required options for the deal.");
        addToCartButton.classList.remove("loading"); // Hide loader in case of error
      }
    });
  }

  // Get selected variants from the selected bar
  getSelectedVariants(selectedBar) {
    const productHandle = selectedBar.dataset.productHandle;
    const productData = this.productDataCache[productHandle];

    if (!productData) {
      console.error("No product data found for handle:", productHandle);
      return [];
    }

    const variantSelects = selectedBar.querySelectorAll(
      ".deal-bundle__bar-variant-selects"
    );
    const selectedVariants = [];

    variantSelects.forEach((selectGroup) => {
      const matchingVariant = this.findMatchingVariant(
        selectGroup,
        productData
      );
      if (matchingVariant) {
        selectedVariants.push({
          id: matchingVariant.id,
          quantity: 1, // Default quantity
        });
      }
    });

    return selectedVariants;
  }

  async addToCart(variants) {
    const formData = { items: variants };

    try {
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Failed to add items to cart: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Cart updated:", data);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  }

  themeCart() {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
      })
    );

    const drawer = document.querySelector("#cart-drawer");
    drawer.setAttribute("open", "");
  }
}

// Define custom element
if (!customElements.get("deal-bundle")) {
  customElements.define("deal-bundle", DealBundle);
}
